import { FC, useCallback, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog, Form, Input } from '@robocorp/components';
import { isSafeName } from '@robocorp/robocloud-shared-utils';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useActions, useSelector } from '~/hooks';
import { useCreateWorkspaceMutation, useOrganizationQuery } from '~/queries';

interface DialogContentProps {
  onClose: () => void;
  organizationId: string;
}

const DialogContent: FC<DialogContentProps> = ({ onClose, organizationId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: organization } = useOrganizationQuery({ organizationId });

  const createWorkspaceMutation = useCreateWorkspaceMutation();

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1)
          .max(255)
          .refine(isSafeName, {
            message: t('nameContainsUrl'),
          }),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    defaultValues: {
      name: '',
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    if (!organization) return;

    createWorkspaceMutation.mutate(
      { organizationId: organization.id, name: data.name },
      {
        onSuccess: (response) => {
          onClose();
          void navigate(`/${organization.short_name}/${response?.short_name}/processes`);
        },
      },
    );
  });

  return (
    <Form onSubmit={onSubmit} busy={createWorkspaceMutation.isLoading}>
      <Dialog.Content>
        <Form.Fieldset>
          <Input
            autoFocus
            label={t('workspaceName')}
            type="text"
            error={errors.name?.message}
            {...register('name')}
          />
        </Form.Fieldset>
      </Dialog.Content>
      <Dialog.Actions>
        <Button type="submit" loading={createWorkspaceMutation.isLoading}>
          {t('create')}
        </Button>
        <Button type="button" onClick={onClose} variant="secondary">
          {t('cancel')}
        </Button>
      </Dialog.Actions>
    </Form>
  );
};

const CreateWorkspaceDialog: FC = () => {
  const { t } = useTranslation();

  const { createWorkspaceForOrg } = useActions((actions) => ({
    createWorkspaceForOrg: actions.uiActions.createWorkspaceForOrg,
  }));

  const { organizationId, creatingWorkspace } = useSelector((state) => ({
    organizationId: state.ui.creatingWorkspaceForOrg.organizationId,
    creatingWorkspace: state.ui.creatingWorkspaceForOrg.creatingWorkspace,
  }));

  const onClose = useCallback(() => {
    createWorkspaceForOrg('');
  }, [createWorkspaceForOrg]);

  return (
    <Dialog open={creatingWorkspace} onClose={onClose} width={500}>
      <Dialog.Header>
        <Dialog.Header.Title title={t('createWorkspace')} />
        <Dialog.Header.Description>{t('workspaceCreateDescription')}</Dialog.Header.Description>
      </Dialog.Header>
      <DialogContent onClose={onClose} organizationId={organizationId} />
    </Dialog>
  );
};

export default CreateWorkspaceDialog;
